/* eslint-disable */
import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SessionContext } from '../../context/sessionContext';
import Logger from '../../logger';
import i18n from '../../localization/i18n';
import * as rk from '../../localization/resourceKeys';
import { StepLayout } from '../components/common/steplayout/StepLayout';
import Loader from '../components/common/loader/loader';
import BannerNotification from '../components/common/banner/bannerNotification';
import EnterAppCode from '../components/myVW/MFA/EnterAppCode';
import EnterEmailCode from '../components/myVW/MFA/EnterEmailCode';
import SetupAuthenticatorApp from '../components/myVW/MFA/SetupAuthenticatorApp';
import { useMyVW } from '../../context/myVWContext';
import { useMyVWQueryParams } from '../hooks/useMyVWQueryParams';
import { triggerMFA, checkAuthenticatorSecretExists, generateAuthenticatorSecret } from '../services/myVW';
import { verifyCustomerRememberMe } from '../services/verifyCustomerRememberMe';
import { getVerifyRememberMeErrorBannerKeys } from '../services/verifyRememberMeErrorMapper';
import { maskEmail } from '../../utilities';

type MFAStep = 'loading' | 'select' | 'enter_email_code' | 'enter_app_code' | 'setup_authenticator';
type MFAMethod = 'email' | 'authenticator';

const MyVWMfa: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const loginAuthenticatorCode = urlParams.get('loginAuthenticatorCode') ?? '';
  const username = urlParams.get('username') ?? '';
  const [step, setStep] = useState<MFAStep>('loading');
  const [method, setMethod] = useState<MFAMethod>('email');
  const { email, partyId: partyIdNum, applyAuthState } = useMyVW();
  const { token, brand, country, buildSearch } = useMyVWQueryParams();

  const { sessionDetails } = useContext(SessionContext)!;
  const myVWSessionDetails = { ...sessionDetails, brand, country };
  const partyId = partyIdNum ?? undefined;

  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [triggerAccountLocked, setTriggerAccountLocked] = useState(false);
  const [maskedDestination, setMaskedDestination] = useState('');
  const [continueLoading, setContinueLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [secretURI, setSecretURI] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyRememberMeError, setVerifyRememberMeError] = useState<{ title?: string; message: string } | null>(null);

  useEffect(() => {
    const masked = maskEmail(email).toLowerCase();
    setMaskedDestination(masked || '');
  }, [email]);

  useEffect(() => {
    if (sessionDetails.correlationId === '') {
      return;
    }
    if (sessionDetails.brand === '' || sessionDetails.country === '') {
      return;
    }
    if (token === '') {
      return;
    }
    if (sessionDetails.source === '') {
      return;
    }

    let stale = false;

    const verify = async () => {
      try {
        const response = await verifyCustomerRememberMe({
          token,
          sessionDetails: myVWSessionDetails,
          username,
          loginAuthenticatorCode,
        });

        if (stale) return;

        const verifyResult = response?.data as Record<string, any>;
        const isSkipMfa = verifyResult?.outcome === 'skipMfa';
        const isRequireMfa = verifyResult?.outcome === 'requireMfa';

        applyAuthState({
          partyId: verifyResult?.partyId,
          email: verifyResult?.email,
          vwfsToken: verifyResult?.token,
          phoneNumber: verifyResult?.phoneNumber,
          accountNumber: verifyResult?.accountNumber,
        });

        if (isSkipMfa) {
          Logger.info('myVWMfa: verifyCustomerRememberMe skipMfa — navigating to dashboard', 'Location ==> /myVW/mfa', sessionDetails);
          navigate('/myVW/dashboard' + buildSearch({
            vwfsToken: verifyResult?.token ?? '',
            accountNumber: verifyResult?.accountNumber ?? '',
            token: '',
            loginAuthenticatorCode: '',
            username: '',
            isLinked: '',
          }), { replace: true });
          return;
        }

        if (isRequireMfa) {
          Logger.info('myVWMfa: verifyCustomerRememberMe requireMfa — showing MFA selection', 'Location ==> /myVW/mfa', sessionDetails);
          // If the URL doesn't already carry a loginAuthenticatorCode (linked-user path arriving
          // from statusDisplay), update the URL with the code returned by verifyCustomerRememberMe
          // so downstream components (authenticator guard, checkAuthenticatorSecretExists) can use it.
          const verifiedCode = verifyResult?.loginAuthenticatorCode ?? '';
          if (verifiedCode && !loginAuthenticatorCode) {
            navigate(location.pathname + buildSearch({ loginAuthenticatorCode: verifiedCode }), { replace: true });
          }
          setStep('select');
          return;
        }

        Logger.error('myVWMfa: verifyCustomerRememberMe contract failure', 'Location ==> /myVW/mfa',
          JSON.stringify({ reasonCode: verifyResult?.reasonCode || 'unknown', correlationId: sessionDetails?.correlationId || '' }),
          sessionDetails,
        );
        const errorBannerKeys = getVerifyRememberMeErrorBannerKeys(verifyResult, verifyResult?.reasonCode);
        setVerifyRememberMeError({
          title: errorBannerKeys.titleKey ? i18n.t(errorBannerKeys.titleKey) : undefined,
          message: i18n.t(errorBannerKeys.messageKey),
        });
      } catch (error) {
        if (stale) return;
        Logger.error('myVWMfa: verifyCustomerRememberMe request failed', 'Location ==> /myVW/mfa', JSON.stringify(error), sessionDetails);
        const errorBannerKeys = getVerifyRememberMeErrorBannerKeys();
        setVerifyRememberMeError({
          title: errorBannerKeys.titleKey ? i18n.t(errorBannerKeys.titleKey) : undefined,
          message: i18n.t(errorBannerKeys.messageKey),
        });
      }
    };

    verify();

    return () => { stale = true; };
  }, [token, sessionDetails.country, sessionDetails.brand, sessionDetails.correlationId, sessionDetails.source]);

  const handleChangeMethod = () => {
    setTriggerAccountLocked(false);
    setStep('select');
  };

  const handleContinue = async () => {
    if (method === 'email') {
      setStep('enter_email_code');
      await handleSendCode();
      return;
    }

    setTriggerAccountLocked(false);
    setTriggerError(null);
    setContinueLoading(true);
    try {
      if (!loginAuthenticatorCode) {
        setTriggerError(i18n.t(rk.ERROR_MYVW_MFA_FAILED));
        return;
      }

      const checkResult = await checkAuthenticatorSecretExists(token, partyId ?? undefined, myVWSessionDetails, loginAuthenticatorCode);
      if (!checkResult.isSuccess) {
        setTriggerError(checkResult.error ?? i18n.t(rk.ERROR_MYVW_MFA_FAILED));
        return;
      }

      if (checkResult.doesSecretExist) {
        setStep('enter_app_code');
        return;
      }

      const genResult = await generateAuthenticatorSecret(token, partyId ?? undefined, myVWSessionDetails, loginAuthenticatorCode);
      if (!genResult.isSuccess) {
        setTriggerError(genResult.error ?? i18n.t(rk.ERROR_MYVW_MFA_FAILED));
        return;
      }

      setSecretURI(genResult.secretURI ?? '');
      setSecret(genResult.secret ?? '');
      setStep('setup_authenticator');
    } catch {
      setTriggerError(i18n.t(rk.ERROR_MYVW_MFA_FAILED));
    } finally {
      setContinueLoading(false);
    }
  };

  const handleSendCode = async () => {
    setTriggerError(null);
    setTriggerAccountLocked(false);
    setTriggerLoading(true);
    try {
      const result = await triggerMFA(token, 'email', partyId ?? undefined, myVWSessionDetails);
      if (!result.isSuccess) {
        if (result.isLocked) {
          setTriggerAccountLocked(true);
        } else {
          setTriggerError(result.error ?? i18n.t(rk.ERROR_MYVW_MFA_FAILED));
        }
        return;
      }
      Logger.info('Sent MFA email code', 'Location ==> myVWMfa', sessionDetails);
    } catch {
      setTriggerError(i18n.t(rk.ERROR_MYVW_MFA_FAILED));
    } finally {
      setTriggerLoading(false);
    }
  };

  if (step === 'loading') {
    if (verifyRememberMeError) {
      return (
        <StepLayout brand={brand} country={country}>
          <div className="u-mt" data-testid="mfa-verify-remember-me-error">
            <BannerNotification notificationStyle="error" title={verifyRememberMeError.title}>
              {verifyRememberMeError.message}
            </BannerNotification>
          </div>
        </StepLayout>
      );
    }

    return (
      <div className="container u-ph-small">
        <Loader isLoading />
      </div>
    );
  }

  if (step === 'setup_authenticator') return (
    <SetupAuthenticatorApp
      onChangeMethod={handleChangeMethod}
      secretURI={secretURI}
      secret={secret}
      triggerError={triggerError}
      triggerAccountLocked={triggerAccountLocked}
      triggerLoading={triggerLoading}
    />
  );
  if (step === 'enter_app_code') return (
    <EnterAppCode
      onChangeMethod={handleChangeMethod}
      triggerError={triggerError}
      triggerAccountLocked={triggerAccountLocked}
      triggerLoading={triggerLoading}
    />
  );
  if (step === 'enter_email_code') return (
    <EnterEmailCode
      onChangeMethod={handleChangeMethod}
      onSendCode={handleSendCode}
      triggerError={triggerError}
      triggerAccountLocked={triggerAccountLocked}
      maskedDestination={maskedDestination}
      triggerLoading={triggerLoading}
    />
  );

  return (
    <StepLayout
      brand={brand}
      country={country}
      title={<h3 className='u-mb-xsmall'>{i18n.t(rk.TITLE_MYVW_MFA_SELECT)}</h3>}
      primaryAction={{
        label: i18n.t(rk.BUTTON_CANCEL),
        onClick: () => navigate('/myVW/login' + buildSearch(), { replace: true }),
        testId: 'mfa-select-cancel',
      }}
      secondaryAction={{
        label: i18n.t(rk.BUTTON_CONTINUE),
        onClick: handleContinue,
        disabled: continueLoading,
        testId: 'mfa-select-continue',
      }}
    >
      <Loader isLoading={continueLoading} />
      {triggerError && (
        <div className="u-mb-small" data-testid="mfa-select-error-banner">
          <BannerNotification notificationStyle="error">
            {triggerError as any}
          </BannerNotification>
        </div>
      )}
      <p className="u-mt">{i18n.t(rk.TEXT_MYVW_MFA_SELECT_P1)}</p>
      <p className="u-mt">{i18n.t(rk.TEXT_MYVW_MFA_SELECT_P2)}</p>
      <div className='o-layout'>
        <div className="o-layout__item">
          <label className="c-radio" htmlFor="mfa-email">
            <input
              id="mfa-email"
              value="email"
              className="c-radio__input"
              type="radio"
              name="mfa-method"
              checked={method === 'email'}
              disabled={continueLoading}
              onChange={() => setMethod('email')}
            />
            <span className="c-radio__label">{i18n.t(rk.BUTTON_MYVW_MFA_EMAIL, { email: maskEmail(email).toLowerCase() })}</span>
          </label>
          <div className='u-ml u-mt-xsmall'>
            <Link
              data-testid="mfa-select-update-email"
              to={'/myVW/update-email' + buildSearch()}
            >
              <span className="c-btn__text">{i18n.t(rk.LINK_MYVW_MFA_UPDATE_EMAIL)}</span>
            </Link>
          </div>
        </div>
        <div className="o-layout__item">
          <label className="c-radio" htmlFor="mfa-authenticator">
            <input
              id="mfa-authenticator"
              value="authenticator"
              className="c-radio__input"
              type="radio"
              name="mfa-method"
              checked={method === 'authenticator'}
              disabled={continueLoading}
              onChange={() => setMethod('authenticator')}
            />
            <span className="c-radio__label">{i18n.t(rk.BUTTON_MYVW_MFA_APP)}</span>
          </label>
        </div>
      </div>
    </StepLayout>
  );
};

export default MyVWMfa;

